/**
 * Data Grid Types
 *
 * Shared type definitions for the editable data grid system.
 * Used by all data-grid hooks and components.
 */

// ---------------------------------------------------------------------------
// Cell position
// ---------------------------------------------------------------------------

/** Identifies a single cell in the grid by visual row index + column ID */
export interface CellPosition {
  /** Visual row index within the current page (0-based) */
  rowIndex: number;
  /** Column ID — matches the TanStack Table column `id` (= Frappe fieldname) */
  columnId: string;
}

// ---------------------------------------------------------------------------
// Range selection (Plan 124)
// ---------------------------------------------------------------------------

/** A rectangular range defined by anchor (start) and focus (end) cells */
export interface CellRange {
  anchor: CellPosition;
  focus: CellPosition;
}

/** Normalized rectangle computed from anchor/focus — used for O(1) hit testing */
export interface NormalizedRect {
  minRow: number;
  maxRow: number;
  minColIdx: number;
  maxColIdx: number;
}

/** Which perimeter edges a cell sits on within a range rectangle */
export interface CellRangePosition {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
}

/** Dimensions of a selected range */
export interface RangeSize {
  rows: number;
  cols: number;
  total: number;
}

// ---------------------------------------------------------------------------
// Cell variants
// ---------------------------------------------------------------------------

/**
 * Cell variant determines which editor component renders for a cell.
 * Epic 01 uses only 'readonly'. Epic 02 adds editable variants.
 */
export type CellVariant =
  | 'text'
  | 'number'
  | 'select'
  | 'checkbox'
  | 'date'
  | 'longText'
  | 'readonly';

// ---------------------------------------------------------------------------
// Data grid state
// ---------------------------------------------------------------------------

/** Full state of the data grid, returned by useDataGrid orchestrator */
export interface DataGridState {
  /** Whether edit mode is active */
  isEditMode: boolean;
  /** Currently selected cell, or null if none */
  activeCell: CellPosition | null;
  /** Set of fieldnames that are editable */
  editableFields: Set<string>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * System fields that are NEVER editable, regardless of editableFields config.
 * These are Frappe core fields present on every DocType.
 */
export const READONLY_SYSTEM_FIELDS = new Set([
  'name',
  'creation',
  'modified',
  'modified_by',
  'owner',
  'docstatus',
  'idx',
]);

// ---------------------------------------------------------------------------
// Hook options / return types
// ---------------------------------------------------------------------------

/** Options for useDataGridSelection */
export interface DataGridSelectionOptions {
  editableFields: Set<string>;
  visibleColumnIds: string[];
  rowCount: number;
  /** Called after a cell is selected (single click). Used to sync range state. */
  onCellSelected?: (pos: CellPosition) => void;
}

/** Options for useDataGridKeyboard */
export interface DataGridKeyboardOptions {
  activeCell: CellPosition | null;
  selectCell: (pos: CellPosition) => void;
  clearSelection: () => void;
  getNextEditableCell: (from: CellPosition) => CellPosition | null;
  getPrevEditableCell: (from: CellPosition) => CellPosition | null;
  getCellBelow: (from: CellPosition) => CellPosition | null;
  getCellAbove: (from: CellPosition) => CellPosition | null;
  getFirstInRow: (rowIndex: number) => CellPosition | null;
  getLastInRow: (rowIndex: number) => CellPosition | null;
  getFirstCellInTable: () => CellPosition | null;
  getLastCellInTable: () => CellPosition | null;
  getCellPageDown: (from: CellPosition, pageSize: number) => CellPosition | null;
  getCellPageUp: (from: CellPosition, pageSize: number) => CellPosition | null;
  isEditMode: boolean;
  /** True when a cell input is focused (not just selected) */
  isCellEditing: boolean;
  /** Called when keyboard commits (Tab/Enter) — editing hook handles save */
  onCommitAndMove?: () => void;
  /** Called when keyboard cancels (Escape) */
  onCancelEdit?: () => void;
  /** Undo/redo/clipboard callbacks — global shortcuts, work even when editing */
  onUndo?: () => void;
  onRedo?: () => void;
  onCopy?: () => void;
  onCut?: () => void;
  onPaste?: () => void;
  onShowHelp?: () => void;
  // Range selection (Plan 124, Epic 03)
  /** Current range (anchor/focus) — non-null even for collapsed single-cell */
  selectionRange: CellRange | null;
  /** Set range for Shift+Arrow extension */
  setRange: (anchor: CellPosition, focus: CellPosition) => void;
  /** Clear range (for collapse on Arrow without Shift) */
  clearRange: () => void;
  /** All visible column IDs in order (excluding __select) — for range column nav */
  visibleColumnIds: string[];
  /** Total row count — for bounds clamping */
  rowCount: number;
  /** Ordered editable column IDs — for Tab/Enter within range */
  editableColumnIds: string[];
  /** Scroll container ref — for scroll-into-view on keyboard range extension */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** Called when Delete/Backspace pressed with multi-cell range active */
  onDelete?: () => void;
}

/** Options for useDataGrid orchestrator */
export interface DataGridOptions {
  editableFields: Set<string>;
  visibleColumnIds: string[];
  rowCount: number;
  isEditMode: boolean;
  /** DocType name for mutations (e.g. "Item") */
  doctype: string;
  /** Callback to get row data by visual row index (from table row model) */
  getRowData: (rowIndex: number) => Record<string, unknown> | null;
  /** Maps fieldname to human-readable label (for paste dialog). Defaults to fieldname. */
  getFieldLabel?: (fieldname: string) => string;
  /** Ref to the scroll container — used for auto-scroll during drag selection */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

/** Return type of useDataGrid orchestrator */
export interface UseDataGridReturn {
  activeCell: CellPosition | null;
  selectCell: (pos: CellPosition) => void;
  clearSelection: () => void;
  isCellActive: (rowIndex: number, columnId: string) => boolean;
  isCellEditable: (columnId: string) => boolean;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  /** True when a cell input is focused (editing text/number) */
  isCellEditing: boolean;
  startEditing: () => void;
  commitEdit: (value: unknown) => void;
  cancelEdit: () => void;
  /** Current uncommitted value in the active cell's input */
  editingValue: unknown;
  getCellSaveState: (rowName: string, fieldname: string) => CellSaveState;
  getRowSaveState: (rowName: string) => 'idle' | 'saving' | 'error';
  // Undo/redo (Epic 03)
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  canUndo: boolean;
  canRedo: boolean;
  // Clipboard (Epic 03)
  copy: () => void;
  cut: () => void;
  paste: () => void;
  /** Non-null when a multi-cell paste is pending confirmation */
  pastePreview: PastePreview | null;
  isPasting: boolean;
  applyPaste: () => Promise<void>;
  cancelPaste: () => void;
  pasteProgress: { current: number; total: number } | null;
  /** True for ~200ms after a range copy — used for visual flash */
  copyFlashActive: boolean;
  // Range selection (Plan 124)
  selectionRange: CellRange | null;
  isCellInRange: (rowIndex: number, columnId: string) => boolean;
  getCellRangePosition: (rowIndex: number, columnId: string) => CellRangePosition | null;
  rangeSize: RangeSize | null;
  setRange: (anchor: CellPosition, focus: CellPosition) => void;
  clearRange: () => void;
  // Mouse drag (Plan 124, Epic 02)
  handleCellMouseDown: (pos: CellPosition, event: React.MouseEvent) => void;
  isDragging: boolean;
  // Help
  isHelpOpen: boolean;
  setHelpOpen: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Mutation / Editing types
// ---------------------------------------------------------------------------

/** Save state for a single cell */
export interface CellSaveState {
  status: 'idle' | 'saving' | 'error';
  error?: string;
  /** True when server returned a newer `modified` than expected (stale row) */
  conflict?: boolean;
}

/** Represents a single cell edit for the mutation hook */
export interface CellEdit {
  doctype: string;
  /** Document name (row identifier) */
  name: string;
  fieldname: string;
  value: unknown;
  oldValue: unknown;
}

// ---------------------------------------------------------------------------
// Undo / Clipboard types (Epic 03)
// ---------------------------------------------------------------------------

/** A single entry in the undo/redo stack */
export interface UndoEntry {
  doctype: string;
  name: string;
  fieldname: string;
  oldValue: unknown;
  newValue: unknown;
  timestamp: number;
  /** Shared ID for batch operations — undo/redo pops all entries with the same batchId */
  batchId?: string;
}

/** Preview data for multi-cell paste dialog */
export interface PastePreview {
  /** Single-column paste: fieldname being pasted into */
  fieldname: string;
  /** Single-column paste: human-readable label */
  fieldLabel: string;
  /** Single-column paste: rows of current→new values */
  rows: Array<{
    name: string;
    rowIndex: number;
    currentValue: unknown;
    newValue: string;
  }>;
  /** 2D paste: column definitions (when pasting across multiple columns) */
  columns?: Array<{ fieldname: string; fieldLabel: string }>;
  /** 2D paste: grid data — outer array = rows, inner array = columns (matches columns order) */
  grid?: Array<Array<{
    name: string;
    rowIndex: number;
    currentValue: unknown;
    newValue: string;
  }>>;
}

/** Props shared by all editable cell variant components */
export interface DataGridCellVariantProps {
  value: unknown;
  onCommit: (newValue: unknown) => void;
  onCancel: () => void;
  onStartEditing: () => void;
  saveState: CellSaveState;
  fieldname: string;
  /** Select/Link field options */
  options?: { label: string; value: string }[];
}
